import { useTranslation } from 'react-i18next';
import { trackHomePageFeaturedSearch } from 'lib/Tracking';
import Heading from 'components/Home/Heading';
import { useInView } from 'react-intersection-observer';
import classnames from 'classnames';

import { screen } from 'components/styles/variables';

// assets
import albanyNY from 'public/static/images/cities/albany-ny.jpg';
import columbusOH from 'public/static/images/cities/columbus-oh.jpg';
import hartfordCT from 'public/static/images/cities/hartford-ct.jpg';
import miamiFL from 'public/static/images/cities/miami-fl.jpg';
import sacramentoCA from 'public/static/images/cities/sacramento-ca.jpg';
import atlantaGA from 'public/static/images/cities/atlanta-ga.jpg';
import denverCO from 'public/static/images/cities/denver-co.jpg';
import houstonTX from 'public/static/images/cities/houston-tx.jpg';
import newYorkNY from 'public/static/images/cities/new-york-ny.jpg';
import springfieldMA from 'public/static/images/cities/springfield-ma.jpg';
import austinTX from 'public/static/images/cities/austin-tx.jpg';
import fortWayneIN from 'public/static/images/cities/fort-wayne-in.jpg';
import lasVegasNV from 'public/static/images/cities/las-vegas-nv.jpg';
import phoenixAZ from 'public/static/images/cities/phoenix-az.jpg';
import springfieldMO from 'public/static/images/cities/springfield-mo.jpg';
import bakersfieldCA from 'public/static/images/cities/bakersfield-ca.jpg';
import fresnoCA from 'public/static/images/cities/fresno-ca.jpg';
import memphisTN from 'public/static/images/cities/memphis-tn.jpg';
import raleighNC from 'public/static/images/cities/raleigh-nc.jpg';
import tampaFL from 'public/static/images/cities/tampa-fl.jpg';

const CITIES = [
  { title: 'Albany, NY', url: '/albany-ny', image: albanyNY },
  { title: 'Atlanta, GA', url: '/atlanta-ga', image: atlantaGA },
  { title: 'Austin, TX', url: '/austin-tx', image: austinTX },
  { title: 'Bakersfield, CA', url: '/bakersfield-ca', image: bakersfieldCA },
  { title: 'Columbus, OH', url: '/columbus-oh', image: columbusOH },
  { title: 'Denver, CO', url: '/denver-co', image: denverCO },
  { title: 'Fort Wayne, IN', url: '/fort+wayne-in', image: fortWayneIN },
  { title: 'Fresno, CA', url: '/fresno-ca', image: fresnoCA },
  { title: 'Hartford, CT', url: '/hartford-ct', image: hartfordCT },
  { title: 'Houston, TX', url: '/houston-tx', image: houstonTX },
  { title: 'Las Vegas, NV', url: '/las+vegas-nv', image: lasVegasNV },
  { title: 'Memphis, TN', url: '/memphis-tn', image: memphisTN },
  { title: 'Miami, FL', url: '/miami-fl', image: miamiFL },
  { title: 'New York, NY', url: '/new+york-ny', image: newYorkNY },
  { title: 'Phoenix, AZ', url: '/phoenix-az', image: phoenixAZ },
  { title: 'Raleigh, NC', url: '/raleigh-nc', image: raleighNC },
  { title: 'Sacramento, CA', url: '/sacramento-ca', image: sacramentoCA },
  { title: 'Springfield, MA', url: '/springfield-ma', image: springfieldMA },
  { title: 'Springfield, MO', url: '/springfield-mo', image: springfieldMO },
  { title: 'Tampa, FL', url: '/tampa-fl', image: tampaFL },
];

export default function CitiesSearches(): JSX.Element {
  const { t } = useTranslation();

  return (
    <div>
      <div className="container">
        <Heading title={t('home.cities.title')} />
        <ul className="cities-container">
          {CITIES.map((citySearch) => (
            <CityCard
              key={citySearch.title}
              title={citySearch.title}
              image={citySearch.image}
              url={citySearch.url}
            />
          ))}
        </ul>
      </div>
      <style jsx>{`
        .cities-container {
          margin: 0;
          padding: 0;
          list-style: none;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-wrap: wrap;
        }
      `}</style>
    </div>
  );
}

interface CityCardProps {
  title: string;
  image: string;
  url: string;
}
function CityCard({ title, image, url }: CityCardProps): JSX.Element {
  const [viewRef, inView] = useInView({ triggerOnce: true });
  const { t } = useTranslation();

  return (
    <li className="city-card" data-location-url={title} ref={viewRef}>
      <a
        href={url}
        onClick={(): void => {
          trackHomePageFeaturedSearch('featured_location', url);
        }}
      >
        <div
          aria-label={t('home.cities.imgAlt', { city: title })}
          className={classnames('city-image-container', { 'city-image-container-view': inView })}
          role="img"
        >
          <h3 className="city-card-title">{title}</h3>
        </div>
      </a>
      <style jsx>
        {`
          .city-card {
            width: 160px;
            margin: 6px;
            border-radius: 6px;
          }

          .city-image-container {
            display: block;
            background-size: cover;
            border-radius: 6px;
          }

          .city-image-container-view {
            background: url(${image});
          }

          .city-card-title {
            margin: 0;
            width: 160px;
            height: 72px;
            border-radius: 6px;
            background-color: rgba(0, 0, 0, 0.4);
            text-align: center;
            line-height: 72px;
            letter-spacing: 0.2px;
            font-size: 14px;
            font-weight: bold;
            color: white;
          }

          .city-card-title:hover {
            background-color: rgba(0, 0, 0, 0.2);
          }

          @media (min-width: ${screen.smMin}) {
            .city-card {
              margin: 8px;
            }
            .city-card-title {
              font-size: 15px;
            }
          }

          @media (min-width: ${screen.mdMin}) {
            .city-card {
              width: 221px;
              margin: 7px;
            }
            .city-image-container {
              height: 99px;
            }

            .city-card-title {
              width: 221px;
              height: 99px;
              line-height: 99px;
              font-size: 18px;
            }
          }

          @media (min-width: ${screen.lgMin}) {
            .city-card {
              width: 256px;
              margin: 12px;
            }
            .city-image-container {
              height: 115px;
            }

            .city-card-title {
              width: 256px;
              height: 115px;
              line-height: 115px;
              font-size: 20px;
            }
          }

          @media (max-width: 360px) {
            .city-card {
              width: 155px;
              margin: 5px;
            }
            .city-card-title {
              width: 155px;
              height: 70px;
            }
          }
          @media (max-width: 320px) {
            .city-card {
              width: 138px;
              margin: 3px;
            }
            .city-card-title {
              width: 138px;
              height: 64px;
            }
          }
        `}
      </style>
    </li>
  );
}
